import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, X, Upload, AlertCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';


export function ContextDocuments({ documents, onDocumentsChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateSummary = async (content) => {
    try {
      const response = await api.post(`/documents/summarize`, { content });
      return response.data.summary;
    } catch (error) {
      console.error('Error generating summary:', error);
      return 'Document uploaded successfully';
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setLoading(true);
    setError(null);

    try {
      const collected = [];
      
      for (const file of acceptedFiles) {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
        }

        let content;

        if (file.type === 'application/pdf') {
          // Upload PDF to extract text
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await api.post(`/pdf/extract-text`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          content = response.data.content;
        } else {
          // Read text files directly
          content = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
          });
        }

        if (!content || content.trim().length === 0) {
          throw new Error('No text content found in file');
        }

        const summary = await generateSummary(content);

        const newDocument = {
          id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          content,
          summary,
          type: 'file',
          filename: file.name
        };
        
        collected.push(newDocument);
      }

      if (collected.length > 0) {
        onDocumentsChange([...documents, ...collected]);
        toast.success(`${collected.length} document(s) uploaded`);
      }
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err.message || 'Failed to process file');
      toast.error(err.message || 'Failed to process file');
    } finally {
      setLoading(false);
    }
  }, [documents, onDocumentsChange]);

  const removeDocument = (id) => {
    onDocumentsChange(documents.filter(doc => doc.id !== id));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center mb-2">
        <FileText className="w-5 h-5 text-blue-500 mr-2" />
        <h3 className="text-sm font-medium text-slate-700">Context Documents (Optional)</h3>
      </div>

      <div
        {...getRootProps()}
        className={`
          p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-all duration-200
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-300 hover:border-blue-400'
          }
        `}
        data-testid="context-dropzone"
      >
        <input {...getInputProps()} />
        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragActive ? 'text-blue-500' : 'text-slate-400'}`} />
        <p className="text-sm font-medium text-slate-700">
          {isDragActive ? 'Drop files here' : 'Drag & drop files, or click to select'}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Supported: TXT, MD, JSON, CSV, PDF (max 10MB)
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
          <p className="text-blue-600 text-sm">Processing documents...</p>
        </div>
      )}

      {error && (
        <div className="flex items-center p-3 bg-red-50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {documents.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            Uploaded Documents ({documents.length})
          </p>
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
            >
              <div className="flex-1 mr-3 min-w-0">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                  <p className="font-medium text-slate-900 text-sm truncate">{doc.filename}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500 line-clamp-2">{doc.summary}</p>
              </div>
              <button
                type="button"
                onClick={() => removeDocument(doc.id)}
                className="text-slate-400 hover:text-red-500 transition-colors p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ContextDocuments;
